import type { ComponentType, SVGProps } from 'react';
import { cn } from '@/lib/cn';

/**
 * Ícones vetoriais curados da exportação visual autorizada do Mobills.
 * Os SVGs foram higienizados e catalogados em docs/mobills-icon-inventory.md.
 * Este módulo usa somente os glifos cuja semântica foi confirmada na navegação.
 */
export type MobillsIconName = 'home' | 'accounts' | 'transactions' | 'cards' | 'reports' | 'more' | 'settings' | 'help';

export type AppIcon = ComponentType<{ className?: string }>;

const shared: SVGProps<SVGSVGElement> = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
};

export function MobillsIcon({ name, className }: { name: MobillsIconName; className?: string }) {
  const svgClass = cn('shrink-0', className);
  switch (name) {
    case 'home':
      return <svg {...shared} className={svgClass}><path d="M3.47 9.16 12 2.53l8.53 6.63v10.42a1.89 1.89 0 0 1-1.9 1.89H5.37a1.89 1.89 0 0 1-1.9-1.89Z" /><path d="M9.16 21.47V12h5.68v9.47" /></svg>;
    case 'accounts':
      return <svg {...shared} className={svgClass}><path d="m12 2.6 9.1 4.7H2.9Zm9.1 18.8H2.9m18.2-2.4H2.9m18.2-8.6H2.9m6.5 8.4V10.7M4.6 18.8V10.7m14.3 8.1V10.7m-4.7 8.1V10.7" /></svg>;
    case 'transactions':
      return <svg {...shared} className={svgClass}><path d="M8.4 7h11.7M8.4 17h11.7M8.4 12H12M3 7a1.3 1.3 0 1 0 2.6 0A1.3 1.3 0 0 0 3 7Zm0 10a1.3 1.3 0 1 0 2.6 0A1.3 1.3 0 0 0 3 17Zm0-5a1.3 1.3 0 1 0 2.6 0A1.3 1.3 0 0 0 3 12Z" /></svg>;
    case 'cards':
      return <svg {...shared} className={svgClass}><path d="M21.3 8.5H2.7m11.8 7H13m3.5 0a1 1 0 1 0 1-1 .9.9 0 0 0-1 1Zm5 1.7V7.1a2.5 2.5 0 0 0-2.6-2.6H5.1A2.5 2.5 0 0 0 2.5 7.1v10.1a2.5 2.5 0 0 0 2.6 2.5h13.8a2.5 2.5 0 0 0 2.6-2.5Z" /></svg>;
    case 'reports':
      return <svg {...shared} className={svgClass}><path d="M19.5 15.1A8.2 8.2 0 0 1 8.8 19.5 8.2 8.2 0 0 1 4.5 8.8a8.4 8.4 0 0 1 4.3-4.2M20.1 12A8.1 8.1 0 0 0 12 3.9V12Z" /></svg>;
    case 'more':
      return <svg {...shared} className={svgClass} fill="currentColor" stroke="none"><circle cx="7" cy="12" r="1.3" /><circle cx="12" cy="12" r="1.3" /><circle cx="17" cy="12" r="1.3" /></svg>;
    case 'settings':
      return <svg {...shared} className={svgClass}><circle cx="12" cy="12" r="3.1" /><path d="M19.6 15.1A1.9 1.9 0 0 0 20 17h.1a2.1 2.1 0 0 1-3 3 1.8 1.8 0 0 0-2.5 0 3.1 3.1 0 0 0-.5 1.2v.2a2.1 2.1 0 1 1-4.1 0 1.7 1.7 0 0 0-1.1-1.6A1.9 1.9 0 0 0 7 20H7a2.2 2.2 0 0 1-3 0 2.2 2.2 0 0 1 0-3h.1a1.8 1.8 0 0 0 0-2.5 3.1 3.1 0 0 0-1.2-.5H2.7a2.1 2.1 0 1 1 0-4.1h.1A1.7 1.7 0 0 0 4.4 8.9 1.9 1.9 0 0 0 4 7H3.9a2.2 2.2 0 0 1 0-3 2.2 2.2 0 0 1 3 0 1.7 1.7 0 0 0 1.9.3h.1a1.5 1.5 0 0 0 1-1.5v-.1A2.2 2.2 0 0 1 12 .6a2.2 2.2 0 0 1 2.1 2.1 1.8 1.8 0 0 0 1.7 1.7A1.8 1.8 0 0 0 17 4a2.2 2.2 0 0 1 3 0 2.2 2.2 0 0 1 0 3h-.1a1.7 1.7 0 0 0-.3 1.9 1.5 1.5 0 0 0 1.5 1h.2a2.2 2.2 0 0 1 2.1 2.1 2.2 2.2 0 0 1-2.1 2.1h-.1a1.8 1.8 0 0 0-1.5 1Z" /></svg>;
    case 'help':
      return <svg {...shared} className={svgClass}><circle cx="12" cy="12" r="10.9" /><path d="M8.8 8.7a3.3 3.3 0 0 1 4.2-2 3.3 3.3 0 0 1 2.2 3.1c0 2.2-3.3 3.3-3.3 3.3" /><path d="M12 17.5h.01" /></svg>;
  }
}

export const MOBILLS_NAV_ICONS: Record<'home' | 'accounts' | 'transactions' | 'cards' | 'reports' | 'more', AppIcon> = {
  home: (props) => <MobillsIcon name="home" {...props} />,
  accounts: (props) => <MobillsIcon name="accounts" {...props} />,
  transactions: (props) => <MobillsIcon name="transactions" {...props} />,
  cards: (props) => <MobillsIcon name="cards" {...props} />,
  reports: (props) => <MobillsIcon name="reports" {...props} />,
  more: (props) => <MobillsIcon name="more" {...props} />,
};
